import { useBackgroundTask } from 'components/BackgroundTaskModal/hooks';
import { useCallback, useMemo, useRef, useState } from 'react';
import { FormattedMessage, type IntlShape } from 'react-intl';
import { MediaResponse } from 'types';
import { useConfirmApi, useProxyParameters } from 'utils/api/ApiHooks';
import ConfirmAPI from 'utils/api/ConfirmAPI';
import { INPUT_TYPES } from 'views/Widgets/Inputs/ValidatedInputTypes';
import {
  GetTicketingInformationResponse,
  TicketingSystemInfo,
  UseTicketingInformationParams,
  UseTicketingInformationReturn,
} from './types';

const DEFAULT_ADDITIONAL_INPUTS = [];
const DEFAULT_TICKETING_INFORMATION = [];

export const FAKE_IMPORT_ACTIVITIES_FROM = (
  formatMessage: IntlShape['formatMessage']
) => ({
  name: 'import_sources',
  type: INPUT_TYPES.CHECKBOX_MULTI,
  label: formatMessage({
    id: 'app.views.widgets.cards.simple_activity_editor_card.incorporate_my_work_from',
    defaultMessage: 'Incorporate my work from:',
  }),
  helperText: formatMessage({
    id: 'app.views.widgets.cards.simple_activity_editor_card.import_sources.helper_text',
    defaultMessage:
      'Select work from any tools that you want incorporated by the AI.',
  }),
  options: [
    { id: 1, name: 'JIRA' },
    { id: 2, name: 'Asana' },
    { id: 3, name: 'Github' },
  ].map((entry) => ({
    key: entry.id,
    label: <span id={String(entry.id)}>{entry.name}</span>,
  })),
});

export const TICKETING_SYSTEMS_INPUT = (
  formatMessage: IntlShape['formatMessage'],
  ticketingSystems: TicketingSystemInfo[]
) => ({
  name: 'import_sources',
  type: INPUT_TYPES.CHECKBOX_MULTI,
  label: formatMessage({
    id: 'app.views.ai.common.ticketing_systems_input.label',
    defaultMessage: 'Incorporate my work from:',
  }),
  helperText: formatMessage({
    id: 'app.views.ai.common.ticketing_systems_input.helper_text',
    defaultMessage:
      'Select work from any tools that you want incorporated by the AI.',
  }),
  defaultValue: ticketingSystems
    .filter((system) => system.enabled)
    .map((system) => system.id),
  options: ticketingSystems.map((system) => ({
    key: system.id,
    label: <span id={system.id}>{system.name}</span>,
    disabled: !system.enabled,
    helperText: system.disabled_explanation || undefined,
  })),
});

export const TICKETING_SYSTEMS_LOADING_INPUT = () => ({
  type: INPUT_TYPES.CUSTOM_INPUT,
  component: (
    <div className="text-center py-3">
      <div className="spinner-border spinner-border-sm me-2" role="status">
        <span className="visually-hidden">
          <FormattedMessage
            id="app.views.ai.common.ticketing_systems_loading_input.loading"
            defaultMessage="Loading..."
          />
        </span>
      </div>
      <span>
        <FormattedMessage
          id="app.views.ai.common.ticketing_systems_loading_input.message"
          defaultMessage="Checking for ticketing platform data..."
        />
      </span>
    </div>
  ),
});

export const GENERIC_AUXILIARY_INPUT = (
  formatMessage: IntlShape['formatMessage'],
  {
    onCallback,
    mockResponse,
    onSubmit,
    onOpen,
    additionalInputs = DEFAULT_ADDITIONAL_INPUTS,
    enableAttachments = true,
  }: {
    onCallback?: (...args: any[]) => any;
    mockResponse?: any;
    onSubmit?: (...args: any[]) => any;
    onOpen?: () => void;
    additionalInputs?: any[];
    enableAttachments?: boolean;
  }
) => {
  return {
    name: 'generate-activity-with-ai',
    title: formatMessage({
      id: 'app.views.ai.common.generic_aux.title',
      defaultMessage: 'Let AI write your first draft',
    }),
    loaderText: formatMessage({
      id: 'app.views.ai.common.generic_aux.loader_text',
      defaultMessage: 'Generating...',
    }),
    submitButtonTitle: formatMessage({
      id: 'app.views.ai.common.generic_aux.submit_button_title',
      defaultMessage: 'Generate',
    }),
    type: 'POPOVER',
    inputs: [
      {
        name: 'prompt',
        helperText: formatMessage({
          id: 'app.views.widgets.cards.simple_activity_editor_card.prompt_helper_text',
          defaultMessage: 'Write any additional instructions for the AI.',
        }),
        label: formatMessage({
          id: 'app.views.widgets.cards.simple_activity_editor_card.prompt_label',
          defaultMessage: 'AI prompt (optional)',
        }),
        placeholder: formatMessage({
          id: 'app.views.widgets.cards.simple_activity_editor_card.prompt_placeholder',
          defaultMessage: 'Specific instructions or additional context.',
        }),
        type: INPUT_TYPES.TEXTAREA,
        maxLength: 1000,
        minRows: 3,
        maxRows: 15,
      },
      ...(enableAttachments
        ? [
            {
              name: 'user_media_file_descriptors',
              helperText: formatMessage({
                id: 'app.views.widgets.cards.simple_activity_editor_card.upload_work_helper_text',
                defaultMessage:
                  'To help the AI, attach any relevant documents, spreadsheets, or presentations.',
              }),
              label: formatMessage({
                id: 'app.views.widgets.cards.simple_activity_editor_card.upload_work',
                defaultMessage: 'Attach files',
              }),
              callToAction: formatMessage({
                id: 'app.views.widgets.cards.simple_activity_editor_card.upload_work.call_to_action',
                defaultMessage: 'Add documents, spreadsheets, or presentations',
              }),
              type: INPUT_TYPES.MEDIA_UPLOADER,
              multiple: true,
              enableDescriptionField: true,
              descriptionFieldPlaceholder: formatMessage({
                id: 'app.views.widgets.inputs.simple_activity_editor_card.description_field_placeholder',
                defaultMessage:
                  'Add a description of the file to give context to the AI',
              }),
              mediaResponseMapper: (mediaResponse: MediaResponse) => ({
                id: mediaResponse.media.id,
                description: mediaResponse?.description,
              }),
              uploadUrl:
                process.env.REACT_APP_CONFIRM_API_URL + '/files/ai-file',
              maxFileSizeMb: 15,
            },
          ]
        : []),
      ...additionalInputs,
    ],
    onCallback: onCallback,
    mockResponse: mockResponse,
    onSubmit: onSubmit,
    onOpen: onOpen,
  };
};

export const suggestionsToObject = (response: any, suggestionType: string) => {
  const newObject = {};

  for (const [key, value] of Object.entries(
    response?.[suggestionType].suggestions
  )) {
    // @ts-expect-error
    const requestType = value.request.type;
    if (requestType === 'TEXTAREA' || requestType === 'RICH_TEXT_EDITOR') {
      // @ts-expect-error
      newObject[key] = { value: value.response.suggestion };
    } else if (
      requestType === 'LIKERT' &&
      // @ts-expect-error
      value?.request?.custom_fields?.singleton
    ) {
      // @ts-expect-error
      const parent_id = value.request.parent_id;
      newObject[parent_id] = {
        value: Number(
          // @ts-expect-error
          value.response.suggestion.value
        ),
        // @ts-expect-error
        description: value.response.suggestion.label,
        singleton: true,
      };
    } else if (
      requestType === 'LIKERT' &&
      // @ts-expect-error
      !value?.request?.custom_fields?.singleton
    ) {
      // @ts-expect-error
      const parent_id = value.request.parent_id;

      if (!newObject[parent_id]) {
        newObject[parent_id] = { value: {}, description: {} };
      }
      newObject[parent_id].value[key] = Number(
        // @ts-expect-error
        value.response.suggestion.value
      );
      newObject[parent_id].description[key] = {
        value: Number(
          // @ts-expect-error
          value.response.suggestion.value
        ),
        // @ts-expect-error
        description: value.response.suggestion.label,
      };
    } else if (
      requestType === 'DROPDOWN' ||
      requestType === 'MULTIPLE_CHOICE'
    ) {
      newObject[key] = {
        // @ts-expect-error
        value: value.response.suggestion.value,
        // @ts-expect-error
        description: value.response.suggestion.label,
      };
    } else if (requestType === 'SKILLS') {
      // @ts-expect-error
      newObject[key] = { value: value.response.suggestion };
    } else if (requestType === 'SWITCH') {
      // @ts-expect-error
      newObject[key] = { value: value.response.suggestion == 'Yes' };
    }
  }

  return newObject;
};

export const getAiSuggestions = (parameters, callback) => {
  ConfirmAPI.sendRequestToConfirm(
    'POST',
    '/ai/suggestions',
    parameters,
    callback
  );
};

enum AI_REQUEST_TYPE {
  AI = 'AI', // Call to OpenAI.
  DEMO = 'DEMO', // For Confirm Sales people, canned responses.
  PREVIEW = 'PREVIEW', // Campaign preview mode, lorem ipsum responses.
}

export const getAiRequestType = (
  isDemoOrPreviewMode: boolean,
  isAiCannedDemoResponsesEnabled: boolean
): AI_REQUEST_TYPE => {
  if (isDemoOrPreviewMode && isAiCannedDemoResponsesEnabled) {
    // Confirmn Sales demoes (they use "preview" mode).
    return AI_REQUEST_TYPE.DEMO;
  }
  if (isDemoOrPreviewMode) {
    return AI_REQUEST_TYPE.PREVIEW;
  }
  return AI_REQUEST_TYPE.AI;
};

type AIBackgroundTaskParams = {
  suggestionType: string;
  requestParameters: object;
  onProgressContentUpdate?: (content: string) => void;
};

export const useAiBackgroundTask = ({
  suggestionType,
  requestParameters,
  onProgressContentUpdate,
}: AIBackgroundTaskParams) => {
  const [backgroundTaskKey, setBackgroundTaskKey] = useState<
    string | undefined
  >(undefined);
  const onDelayedCallbackRef = useRef();
  const onDelayedHandleCloseRef = useRef();
  const onDelayedHandleErrorRef = useRef();

  const handleOnTaskComplete = useCallback((data) => {
    setBackgroundTaskKey(undefined);
    if (onDelayedCallbackRef.current) {
      // @ts-expect-error
      onDelayedCallbackRef.current(data);
      onDelayedCallbackRef.current = undefined;
    }
    if (onDelayedHandleCloseRef.current) {
      // @ts-expect-error
      onDelayedHandleCloseRef.current();
      onDelayedHandleCloseRef.current = undefined;
    }
    if (onDelayedHandleErrorRef.current) {
      onDelayedHandleErrorRef.current = undefined;
    }
  }, []);

  const handleOnTaskError = useCallback((data) => {
    setBackgroundTaskKey(undefined);
    if (onDelayedHandleErrorRef.current) {
      // @ts-expect-error
      onDelayedHandleErrorRef.current(data);
      onDelayedHandleErrorRef.current = undefined;
    }
    if (onDelayedCallbackRef.current) {
      onDelayedCallbackRef.current = undefined;
    }
    if (onDelayedHandleCloseRef.current) {
      onDelayedHandleCloseRef.current = undefined;
    }
  }, []);

  const handleOnProgressContentUpdate = useCallback(
    (content: string) => {
      onProgressContentUpdate?.(content);
    },
    [onProgressContentUpdate]
  );

  const proxyParameters = useProxyParameters();

  const requestSuggestion = useCallback(
    (object, onCallback, handleClose, handleError) => {
      getAiSuggestions(
        {
          suggestion_type: suggestionType,
          ...proxyParameters,
          ...requestParameters,
          ...object,
        },
        (response, error) => {
          if (!!response && !error) {
            setBackgroundTaskKey(response?.background_task_key);
            onDelayedCallbackRef.current = onCallback;
            onDelayedHandleCloseRef.current = handleClose;
            onDelayedHandleErrorRef.current = handleError;
          }
        }
      );
    },
    [suggestionType, proxyParameters, requestParameters]
  );

  useBackgroundTask({
    disabled: !backgroundTaskKey,
    taskDescriptor: {
      // @ts-expect-error
      task_key: backgroundTaskKey,
      status: 'submitted',
    },
    onTaskCompleted: handleOnTaskComplete,
    onError: handleOnTaskError,
    onProgressContentUpdate: handleOnProgressContentUpdate,
  });

  const resumeTask = useCallback(
    (
      taskKey: string,
      onCallback: (data: any) => void,
      handleClose: () => void,
      handleError: (error: any) => void
    ) => {
      onDelayedCallbackRef.current = onCallback as any;
      onDelayedHandleCloseRef.current = handleClose as any;
      onDelayedHandleErrorRef.current = handleError as any;
      setBackgroundTaskKey(taskKey);
    },
    []
  );

  const cancelTask = useCallback(() => {
    setBackgroundTaskKey(undefined);
    onDelayedCallbackRef.current = undefined;
    onDelayedHandleCloseRef.current = undefined;
    onDelayedHandleErrorRef.current = undefined;
  }, []);

  const value = useMemo(
    () => ({
      requestSuggestion,
      resumeTask,
      cancelTask,
      backgroundTaskKey,
    }),
    [requestSuggestion, resumeTask, cancelTask, backgroundTaskKey]
  );

  return value;
};

export const useTicketingInformation = ({
  campaign,
  contextPersonId,
  lazy = false,
}: UseTicketingInformationParams): UseTicketingInformationReturn => {
  const [shouldFetch, setShouldFetch] = useState(!lazy);

  const { data, status, error } =
    useConfirmApi<GetTicketingInformationResponse>({
      method: 'GET',
      url: '/ticketing-information',
      params: {
        context_person_id: contextPersonId,
        coverage_start_date: campaign?.coverage_start_date,
        coverage_end_date: campaign?.coverage_end_date,
        campaign_id: campaign?.id,
      },
      disabled: !shouldFetch || !campaign || !contextPersonId,
      clearOnChange: true,
    });

  const fetch = useCallback(() => {
    setShouldFetch(true);
  }, []);

  const ticketingSystems =
    data?.ticketing_information || DEFAULT_TICKETING_INFORMATION;
  const loading = status === 'LOADING';

  return { ticketingSystems, loading, error, fetch };
};
